import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Gift, 
  Coins, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Wallet,
  Trophy,
  Star,
  Calendar,
  TrendingUp,
  Zap,
  Download,
  ExternalLink
} from 'lucide-react';
import BackButton from '../components/BackButton';

export default function ClaimPlyp() {
  const { isLoggedIn, address } = useAuth();
  
  // 状态管理
  const [claimableAmount, setClaimableAmount] = useState(0);
  const [totalClaimed, setTotalClaimed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastClaimTime, setLastClaimTime] = useState(null);
  const [nextClaimTime, setNextClaimTime] = useState(null);
  const [claimHistory, setClaimHistory] = useState([]);

  // 模拟数据初始化
  useEffect(() => {
    if (isLoggedIn) {
      // 模拟从区块链获取数据
      setClaimableAmount(125.50);
      setTotalClaimed(1250.75);
      setLastClaimTime(new Date(Date.now() - 6 * 60 * 60 * 1000)); // 6小时前
      setNextClaimTime(new Date(Date.now() + 18 * 60 * 60 * 1000)); // 18小时后
      
      // 模拟领取历史
      setClaimHistory([
        {
          id: 1,
          amount: 50.25,
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
          txHash: '0x1234...5678',
          reason: 'Daily Reward'
        },
        {
          id: 2,
          amount: 100.50,
          timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000),
          txHash: '0x2345...6789',
          reason: 'Prediction Bonus'
        },
        {
          id: 3,
          amount: 75.00,
          timestamp: new Date(Date.now() - 54 * 60 * 60 * 1000),
          txHash: '0x3456...7890',
          reason: 'Weekly Bonus'
        }
      ]);
    }
  }, [isLoggedIn]);

  // 计算下次领取倒计时
  const [timeUntilNextClaim, setTimeUntilNextClaim] = useState('');
  
  useEffect(() => {
    if (nextClaimTime) {
      const updateCountdown = () => {
        const now = new Date();
        const diff = nextClaimTime - now;
        
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setTimeUntilNextClaim(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setTimeUntilNextClaim('Available now!');
          setClaimableAmount(prev => prev + 25); // 新的可领取金额
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [nextClaimTime]);

  // 领取代币函数
  const handleClaim = async () => {
    if (claimableAmount <= 0 || isLoading) return;
    
    setIsLoading(true);
    
    try {
      // 模拟区块链交易
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // 更新状态
      setTotalClaimed(prev => prev + claimableAmount);
      const claimedAmount = claimableAmount;
      setClaimableAmount(0);
      setLastClaimTime(new Date());
      setNextClaimTime(new Date(Date.now() + 24 * 60 * 60 * 1000)); // 24小时后
      
      // 添加到历史记录
      const newClaim = {
        id: Date.now(),
        amount: claimedAmount,
        timestamp: new Date(),
        txHash: `0x${Math.random().toString(16).substr(2, 8)}...${Math.random().toString(16).substr(2, 4)}`,
        reason: 'Manual Claim'
      };
      setClaimHistory(prev => [newClaim, ...prev]);
      
    } catch (error) {
      console.error('Claim failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="bg-plypto-bg min-h-screen">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <BackButton />
              <div className="h-6 w-px bg-white/20"></div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Claim Plyp Tokens</h1>
                  <p className="text-gray-400">Claim your earned PLYP tokens from successful predictions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                <Coins className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Claim Plyp Tokens</h1>
                <p className="text-gray-400">Claim your earned PLYP tokens from successful predictions</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Main Claim Interface */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Claim Card */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-8 border border-purple-500/20">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Coins className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">Available to Claim</h2>
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-6">
                  {claimableAmount.toFixed(2)} PLYP
                </div>
                
                {claimableAmount > 0 ? (
                  <button
                    onClick={handleClaim}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Claiming...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        <span>Claim Now</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="text-center">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                      <Clock className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">Next claim available in:</p>
                      <p className="text-white font-semibold">{timeUntilNextClaim}</p>
                    </div>
                    <button
                      disabled
                      className="w-full bg-gray-600 text-gray-400 font-semibold py-4 px-8 rounded-xl cursor-not-allowed"
                    >
                      No Tokens Available
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Earning Methods */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span>How to Earn PLYP</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="font-medium text-white">Successful Predictions</span>
                  </div>
                  <p className="text-gray-400 text-sm">Earn 5-50 PLYP for each winning prediction</p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-3 mb-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-white">Daily Login</span>
                  </div>
                  <p className="text-gray-400 text-sm">Get 10 PLYP every day you log in</p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-3 mb-2">
                    <Trophy className="w-5 h-5 text-purple-400" />
                    <span className="font-medium text-white">Weekly Challenges</span>
                  </div>
                  <p className="text-gray-400 text-sm">Complete weekly goals for bonus rewards</p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-3 mb-2">
                    <Zap className="w-5 h-5 text-orange-400" />
                    <span className="font-medium text-white">Referral Bonus</span>
                  </div>
                  <p className="text-gray-400 text-sm">Earn 25 PLYP for each friend you refer</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Stats and History */}
          <div className="space-y-6">
            
            {/* Stats Card */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Your Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Total Claimed</span>
                  <span className="text-white font-semibold">{totalClaimed.toFixed(2)} PLYP</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Last Claim</span>
                  <span className="text-white font-semibold">
                    {lastClaimTime ? lastClaimTime.toLocaleDateString() : 'Never'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Wallet Address</span>
                  <span className="text-white font-mono text-sm">
                    {address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Not connected'}
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Claims */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <span>Recent Claims</span>
              </h3>
              
              <div className="space-y-3">
                {claimHistory.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No claims yet</p>
                ) : (
                  claimHistory.slice(0, 5).map(claim => (
                    <div key={claim.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">+{claim.amount} PLYP</span>
                        <span className="text-gray-400 text-xs">
                          {claim.timestamp.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">{claim.reason}</span>
                        <button className="text-blue-400 hover:text-blue-300 text-xs flex items-center space-x-1">
                          <span>{claim.txHash}</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Token Info */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">About PLYP Token</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-2">
                  <Coins className="w-4 h-4 text-purple-400" />
                  <span className="text-gray-400">Utility token for the Plypto platform</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Wallet className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400">Used for premium features and rewards</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-gray-400">Tradeable on decentralized exchanges</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
